import React, { useEffect } from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { ClientDetailForm } from 'Components/ComponentLibrary/Organisms/ClientDetailForm/ClientDetailForm';
import {
  FDConfirmationDialog,
  FDContentContainer,
  FDPaper,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import { dispatchModalItemDeletedRefresh } from 'Hooks/useRefreshEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useProfilePageSubmissionContext } from 'Views/Page/Content/Templates/ProfilePage/ProfilePageSubmissionContext';
import { dataAttrs } from 'Helpers/Testing';

export const ClientLayout = ({ onSuccessfulSubmit }) => {
  const {
    resource, onSubmit, onDelete, saving,
  } = useMetaLayoutContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { setProfilePageSubmit } = useProfilePageSubmissionContext();

  useEffect(() => {
    setProfilePageSubmit(async () => {
      const res = await onSubmit();
      if (!res) {
        throw new Error('error submitting');
      }
    });
    return () => setProfilePageSubmit(() => {});
  }, [onSubmit]);

  const {
    dialogOpen,
    loading,
    handleOpen,
    handleSubmit: handleDelete, // In this case the submit action of the dialog will actually delete the resource
    handleClose,
  } = useConfirmationDialogState({
    onSubmit: () => onDelete(resource),
    closeOnSubmit: true,
  });

  async function handleConfirm() {
    try {
      await handleDelete();
      dispatchModalItemDeletedRefresh();
    } catch (err) {
      displayErrorMessage(err, err?.errors || 'Failed to remove item.');
    }
    return undefined;
  }

  return (
    <FDContentContainer maxWidth={'md'}>
      <FDPaper appearance={'elevated'} padding={400} width={'100%'}>
        <FDStack direction={'column'} spacing={4}>
          <ClientDetailForm
            onProfileImageUpload={onSuccessfulSubmit}
          />
          <FDStack justifyContent={'flex-end'} spacing={2}>
            {resource.meta().canDelete() ? (
              <FDSecondaryActionButton
                onClick={handleOpen}
                label={'Remove Member'}
              />
            ) : null}
            <FDPrimaryActionButton
              onClick={() => onSubmit()}
              loading={saving}
              label={'Save Changes'}
              {...dataAttrs('profile-page-save-btn')}
            />
          </FDStack>
        </FDStack>
        <FDConfirmationDialog
          message={'Removing this household member will delete all of their information. This cannot be undone.'}
          title={'Are you sure?'}
          cancelLabel={'Nevermind'}
          onConfirm={handleConfirm}
          confirmLabel={'Remove'}
          onClose={handleClose}
          open={dialogOpen}
          loading={loading}
        />
      </FDPaper>
    </FDContentContainer>
  );
};

ClientLayout.propTypes = {
  onSuccessfulSubmit: PropTypes.func.isRequired,
};
